import React, { useEffect, useRef, useState } from 'react';

// Distances and segment endpoints are in the stitched image's natural pixels,
// never in CSS pixels. The SVG viewBox scales the overlay with the image.
const UNITS = { mm: 0.001, cm: 0.01, m: 1, in: 0.0254, ft: 0.3048, yd: 0.9144 };
const length = (line) => Math.hypot(line.end.x - line.start.x, line.end.y - line.start.y);
const format = (value) => Number(value.toFixed(2)).toString();
const validPoint = (p, w, h) => p && Number.isFinite(p.x) && Number.isFinite(p.y) &&
  p.x >= 0 && p.y >= 0 && p.x <= w && p.y <= h;
const validLine = (line, w, h) => line && validPoint(line.start, w, h) &&
  validPoint(line.end, w, h) && length(line) > 0;

const MosaicDistanceViewer = ({ imageUrl, imageId, mosaicId, savedMeasurements, endpoint, onClose }) => {
  const [dimensions, setDimensions] = useState(null);
  const [mode, setMode] = useState('reference');
  const [reference, setReference] = useState(null);
  const [measurements, setMeasurements] = useState([]);
  const [draft, setDraft] = useState(null);
  const [referenceValue, setReferenceValue] = useState('1');
  const [unit, setUnit] = useState('m');
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [showMobileHelp, setShowMobileHelp] = useState(false);
  const svgRef = useRef(null);
  const drawingRef = useRef(null);
  const panRef = useRef(null);
  const touchPointersRef = useRef(new Map());
  const touchGestureRef = useRef(null);
  const imageRef = useRef(null);
  const viewportRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [restored, setRestored] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const saveTimerRef = useRef(null);
  const saveStateRef = useRef(null);
  const saveQueueRef = useRef(Promise.resolve());
  // Preserve the last known-good calibration while the number field is empty
  // or invalid during an edit. Never invent a reference value on autosave.
  const lastValidCalibrationRef = useRef({ value: 1, unit: 'm' });
  const persistedReferenceValue = (() => {
    const current = Number(referenceValue);
    if (referenceValue.trim() !== '' && Number.isFinite(current) && current > 0) return current;
    const last = lastValidCalibrationRef.current;
    return last.value * UNITS[last.unit] / UNITS[unit];
  })();
  const key = `cs2n-mosaic-measurements:${mosaicId}:${imageUrl}`;

  useEffect(() => {
    const image = new Image();
    let canceled = false;
    image.onload = () => {
      if (canceled) return;
      setDimensions({ width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = () => { if (!canceled) setError('Unable to load the stitched image.'); };
    image.src = imageUrl;
    return () => { canceled = true; };
  }, [imageUrl]);

  useEffect(() => {
    if (!dimensions) return;
    try {
      const stored = savedMeasurements?.image_id === imageId && savedMeasurements?.width === dimensions.width && savedMeasurements?.height === dimensions.height
        ? savedMeasurements
        : JSON.parse(window.localStorage.getItem(key) || 'null');
      if (!stored || stored.width !== dimensions.width || stored.height !== dimensions.height) return;
      if (validLine(stored.reference, dimensions.width, dimensions.height) &&
          Number.isFinite(stored.referenceValue) && stored.referenceValue > 0 &&
          UNITS[stored.unit]) {
        setReference(stored.reference);
        setReferenceValue(String(stored.referenceValue));
        lastValidCalibrationRef.current = { value: stored.referenceValue, unit: stored.unit };
        setUnit(stored.unit);
        setMode('measure');
        setMeasurements(Array.isArray(stored.measurements)
          ? stored.measurements.filter((line) => validLine(line, dimensions.width, dimensions.height)).slice(0, 100)
          : []);
      }
    } catch (_) {
      // Missing or invalid browser storage should not prevent measuring.
    } finally {
      setRestored(true);
    }
  }, [key, dimensions?.width, dimensions?.height]);

  useEffect(() => {
    if (!dimensions || !restored) return;
    try {
      window.localStorage.setItem(key, JSON.stringify({
        width: dimensions.width, height: dimensions.height,
        reference, referenceValue: persistedReferenceValue, unit, measurements,
      }));
    } catch (_) {
      // Measurement tools still work if private browsing blocks storage.
    }
  }, [key, dimensions, restored, reference, referenceValue, unit, measurements]);

  // Persist all changes on the authenticated mosaic submission, not only in
  // browser storage. Closing flushes pending edits before dismissing the viewer.
  const saveNow = (payload) => {
    if (!imageId || !endpoint || !payload) return Promise.resolve(false);
    // Serialize saves so an older request cannot overwrite the newest edits.
    const request = saveQueueRef.current.catch(() => {}).then(() =>
      endpoint.patch(`/client_api/v1/mixed_medias/${mosaicId}/update_image_mosaic_measurements`, {
        image_id: imageId, measurement: payload,
      })
    );
    saveQueueRef.current = request;
    return request.then(() => {
      setSaveStatus('Saved');
      return true;
    }).catch(() => {
      setSaveStatus('Could not save changes');
      setError('Could not save measurements to CS2N. Please retry before closing.');
      return false;
    });
  };

  useEffect(() => {
    if (!dimensions || !restored || !imageId) return undefined;
    const payload = {
      width: dimensions.width,
      height: dimensions.height,
      reference_value: persistedReferenceValue,
      unit,
      reference,
      lines: measurements,
    };
    saveStateRef.current = payload;
    setSaveStatus('Saving…');
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      saveNow(payload);
    }, 650);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [dimensions, restored, imageId, reference, referenceValue, unit, measurements]);

  const closeAndSave = async () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
      if (!await saveNow(saveStateRef.current)) return;
    } else if (saveQueueRef.current) {
      try { await saveQueueRef.current; } catch (_) { return; }
    }
    onClose();
  };

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (drawingRef.current) {
          drawingRef.current = null;
          setDraft(null);
        } else closeAndSave();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const point = (event) => {
    if (!svgRef.current) return null;
    const p = svgRef.current.createSVGPoint();
    p.x = event.clientX;
    p.y = event.clientY;
    const matrix = svgRef.current.getScreenCTM();
    if (!matrix) return null;
    const result = p.matrixTransform(matrix.inverse());
    return { x: result.x, y: result.y };
  };
  const inside = (p) => dimensions && validPoint(p, dimensions.width, dimensions.height);

  const start = (event) => {
    if (event.pointerType === 'touch') {
      const pointers = touchPointersRef.current;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      event.currentTarget.setPointerCapture(event.pointerId);
      if (pointers.size >= 2) {
        drawingRef.current = null;
        setDraft(null);
        const [a, b] = [...pointers.values()];
        touchGestureRef.current = {
          distance: Math.hypot(a.x - b.x, a.y - b.y),
          center: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
          zoom, pan,
        };
        return;
      }
    }
    if (event.button === 1 || (event.button === 0 && event.shiftKey)) {
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      panRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, pan };
      return;
    }
    if (event.button !== 0) return;
    if (mode === 'measure' && (!reference || !(Number(referenceValue) > 0))) return;
    const p = point(event);
    if (!inside(p)) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    drawingRef.current = { pointerId: event.pointerId, start: p };
    setDraft({ start: p, end: p });
  };
  const move = (event) => {
    if (event.pointerType === 'touch') {
      const pointers = touchPointersRef.current;
      if (!pointers.has(event.pointerId)) return;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (pointers.size >= 2) {
        const [a, b] = [...pointers.values()];
        const gesture = touchGestureRef.current;
        if (!gesture || !viewportRef.current) return;
        const center = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        const nextZoom = Math.max(1, Math.min(12,
          gesture.zoom * Math.hypot(a.x - b.x, a.y - b.y) / Math.max(1, gesture.distance)));
        const rect = viewportRef.current.getBoundingClientRect();
        const cx = gesture.center.x - rect.left - rect.width / 2;
        const cy = gesture.center.y - rect.top - rect.height / 2;
        const factor = nextZoom / gesture.zoom;
        setZoom(nextZoom);
        setPan(nextZoom === 1 ? { x: 0, y: 0 } : {
          x: gesture.pan.x + (1 - factor) * (cx - gesture.pan.x) + center.x - gesture.center.x,
          y: gesture.pan.y + (1 - factor) * (cy - gesture.pan.y) + center.y - gesture.center.y,
        });
        return;
      }
      if (touchGestureRef.current) return;
    }
    if (panRef.current?.pointerId === event.pointerId) {
      const p = panRef.current;
      setPan({ x: p.pan.x + event.clientX - p.x, y: p.pan.y + event.clientY - p.y });
      return;
    }
    if (!drawingRef.current || drawingRef.current.pointerId !== event.pointerId) return;
    const p = point(event);
    if (!p || !dimensions) return;
    const end = {
      x: Math.max(0, Math.min(dimensions.width, p.x)),
      y: Math.max(0, Math.min(dimensions.height, p.y)),
    };
    setDraft({ start: drawingRef.current.start, end });
  };
  const finish = (event) => {
    if (event.pointerType === 'touch') {
      touchPointersRef.current.delete(event.pointerId);
      if (touchGestureRef.current) {
        drawingRef.current = null;
        setDraft(null);
        if (touchPointersRef.current.size === 0) touchGestureRef.current = null;
        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
        return;
      }
    }
    if (panRef.current?.pointerId === event.pointerId) {
      panRef.current = null;
      if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
      return;
    }
    if (!drawingRef.current || drawingRef.current.pointerId !== event.pointerId) return;
    const p = point(event);
    const origin = drawingRef.current.start;
    drawingRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setDraft(null);
    if (!p || !dimensions) return;
    const line = { start: origin, end: {
      x: Math.max(0, Math.min(dimensions.width, p.x)),
      y: Math.max(0, Math.min(dimensions.height, p.y)),
    } };
    if (!validLine(line, dimensions.width, dimensions.height) || length(line) < 2) return;
    if (mode === 'reference') {
      setReference(line);
      setMeasurements([]);
      setMode('measure');
      setError('');
    } else {
      setMeasurements((current) => [...current, line]);
    }
  };

  const handleWheel = (event) => {
    if (!imageRef.current) return;
    event.preventDefault();
    const newZoom = Math.min(12, Math.max(1, zoom * (event.deltaY < 0 ? 1.2 : 1 / 1.2)));
    if (newZoom === zoom) return;
    // Pan is relative to the center of the viewport, not the image's
    // transformed bounding rectangle. This keeps the cursor's point stationary.
    const rect = viewportRef.current.getBoundingClientRect();
    const factor = newZoom / zoom;
    const mouseX = event.clientX - rect.left - rect.width / 2;
    const mouseY = event.clientY - rect.top - rect.height / 2;
    setPan((current) => newZoom === 1 ? { x: 0, y: 0 } : {
      x: current.x + (1 - factor) * (mouseX - current.x),
      y: current.y + (1 - factor) * (mouseY - current.y),
    });
    setZoom(newZoom);
  };

  const referenceMeters = Number(referenceValue) * UNITS[unit];
  const calibrated = Boolean(reference && Number.isFinite(referenceMeters) && referenceMeters > 0);
  const distance = (line) => calibrated ? (length(line) / length(reference)) * Number(referenceValue) : null;
  const downloadAnnotatedImage = async () => {
    if (!dimensions || !calibrated || exporting) return;
    setExporting(true);
    setError('');
    try {
      // The source image may be hosted on S3. Load using CORS so the canvas
      // remains exportable. An unsupported S3 CORS policy is reported clearly.
      const source = new Image();
      source.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => {
        source.onload = resolve;
        source.onerror = () => reject(new Error('Could not load the mosaic for export. Check image access/CORS permissions.'));
        source.src = imageUrl;
      });
      const canvas = document.createElement('canvas');
      // Bound allocation for browser canvas and memory limits (including iOS Safari).
      const maxPixels = 8_000_000;
      const maxSide = 4096;
      const exportScale = Math.min(1, Math.sqrt(maxPixels / (dimensions.width * dimensions.height)),
        maxSide / Math.max(dimensions.width, dimensions.height));
      canvas.width = Math.max(1, Math.floor(dimensions.width * exportScale));
      canvas.height = Math.max(1, Math.floor(dimensions.height * exportScale));
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Image export is unavailable in this browser.');
      ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
      ctx.scale(canvas.width / dimensions.width, canvas.height / dimensions.height);

      // Draw the annotations using natural image coordinates. Label sizes stay
      // readable in the exported full-resolution file.
      const ratio = Math.max(1, Math.min(dimensions.width, dimensions.height) / 900);
      const drawAnnotation = (line, color, text) => {
        ctx.save();
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.lineWidth = 6 * ratio;
        ctx.strokeStyle = '#ffffff';
        ctx.beginPath();
        ctx.moveTo(line.start.x, line.start.y);
        ctx.lineTo(line.end.x, line.end.y);
        ctx.stroke();
        ctx.strokeStyle = color;
        ctx.lineWidth = 4 * ratio;
        ctx.stroke();
        [line.start, line.end].forEach((p) => {
          ctx.beginPath();
          ctx.arc(p.x, p.y, 5 * ratio, 0, 2 * Math.PI);
          ctx.fillStyle = color;
          ctx.fill();
        });
        const centerX = (line.start.x + line.end.x) / 2;
        const centerY = (line.start.y + line.end.y) / 2;
        ctx.font = `bold ${18 * ratio}px sans-serif`;
        const pad = 6 * ratio;
        const width = ctx.measureText(text).width + 2 * pad;
        const height = 28 * ratio;
        const x = Math.max(0, Math.min(dimensions.width - width, centerX - width / 2));
        const y = Math.max(0, Math.min(dimensions.height - height, centerY - height - 13 * ratio));
        ctx.fillStyle = 'rgba(17, 28, 42, 0.83)';
        ctx.fillRect(x, y, width, height);
        ctx.strokeStyle = color;
        ctx.lineWidth = 2 * ratio;
        ctx.strokeRect(x, y, width, height);
        ctx.textBaseline = 'middle';
        ctx.fillStyle = '#ffffff';
        ctx.fillText(text, x + pad, y + height / 2);
        ctx.restore();
      };

      drawAnnotation(reference, '#f04444', `Reference: ${format(Number(referenceValue))} ${unit}`);
      measurements.forEach((line, index) => {
        drawAnnotation(line, '#2688ee', `#${index + 1}: ${format(distance(line))} ${unit}`);
      });

      const blob = await new Promise((resolve, reject) => canvas.toBlob(
        (result) => result ? resolve(result) : reject(new Error('Unable to generate the annotated PNG.')),
        'image/png'
      ));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mosaic-${mosaicId}-measurements.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError(e.message || 'Could not export the annotated mosaic.');
    } finally {
      setExporting(false);
    }
  };

  const resetReference = () => {
    drawingRef.current = null;
    setDraft(null);
    setReference(null);
    setMeasurements([]);
    setReferenceValue('1');
    lastValidCalibrationRef.current = { value: 1, unit };
    setMode('reference');
    setError('');
  };

  // Render only strokes in SVG. Browser-sized HTML labels are never scaled by
  // the SVG image viewBox, so they remain legible on large mosaics.
  const drawLine = (line, color, keyValue) => (
    <g key={keyValue} pointerEvents="none">
      <line x1={line.start.x} y1={line.start.y} x2={line.end.x} y2={line.end.y}
        stroke="#fff" strokeWidth="5" vectorEffect="non-scaling-stroke" />
      <line x1={line.start.x} y1={line.start.y} x2={line.end.x} y2={line.end.y}
        stroke={color} strokeWidth="3" vectorEffect="non-scaling-stroke" />
      <circle cx={line.start.x} cy={line.start.y} r="4" fill={color} stroke="#fff"
        strokeWidth="1" vectorEffect="non-scaling-stroke" />
      <circle cx={line.end.x} cy={line.end.y} r="4" fill={color} stroke="#fff"
        strokeWidth="1" vectorEffect="non-scaling-stroke" />
    </g>
  );

  const lineLabel = (line, color, label, keyValue) => {
    if (!dimensions || !label) return null;
    const x = ((line.start.x + line.end.x) / 2 / dimensions.width) * 100;
    const y = ((line.start.y + line.end.y) / 2 / dimensions.height) * 100;
    return (
      <div key={keyValue} style={{
        position: 'absolute', left: `${x}%`, top: `${y}%`,
        transform: `translate(-50%, calc(-100% - ${12 / zoom}px))`,
        pointerEvents: 'none', zIndex: 2, maxWidth: '90%',
        background: 'rgba(17, 28, 42, 0.83)', color: '#fff',
        border: `1px solid ${color}`, borderRadius: 5 / zoom,
        padding: `${3 / zoom}px ${7 / zoom}px`, fontSize: `${16 / zoom}px`, lineHeight: `${20 / zoom}px`,
        fontWeight: 700, whiteSpace: 'nowrap', width: 'max-content',
        textShadow: '0 1px 2px #000',
      }}>{label}</div>
    );
  };

  return (
    <div role="dialog" aria-modal="true" aria-label="Measure mosaic distances" className="mosaic-measure-dialog"
      style={{ position: 'fixed', inset: 0, zIndex: 11000, background: '#161c25', color: '#fff',
        display: 'flex', flexDirection: 'column', padding: 16, overflow: 'auto' }}>
      <style>{`
        .mosaic-measure-dialog { box-sizing: border-box; }
        .mosaic-measure-dialog *, .mosaic-measure-dialog *::before, .mosaic-measure-dialog *::after { box-sizing: border-box; }
        .mosaic-measure-help-toggle { display: none; }
        @media (max-width: 700px) {
          .mosaic-measure-dialog { padding: 10px !important; overflow-y: auto !important; }
          .mosaic-measure-toolbar { position: sticky; top: 0; z-index: 5; background: #161c25; }
          .mosaic-measure-toolbar { display: grid !important; grid-template-columns: minmax(0, 1fr) auto; grid-template-areas: "modes close" "settings settings"; align-items: center !important; gap: 7px !important; }
          .mosaic-measure-toolbar-scroll { display: contents !important; }
          .mosaic-measure-mode-group { grid-area: modes; min-width: 0; display: grid !important; grid-template-columns: 1fr 1fr; gap: 6px !important; }
          .mosaic-measure-mode-group .btn { min-width: 0; padding: 8px 4px !important; font-size: 12px; white-space: normal !important; line-height: 1.15; height: 42px; }
          .mosaic-measure-close { grid-area: close; padding: 8px 10px !important; height: 42px; }
          .mosaic-measure-settings { grid-area: settings; display: flex !important; align-items: center; gap: 7px; min-width: 0; }
          .mosaic-measure-settings-label { flex: 1 1 0; min-width: 0; font-size: 12px; }
          .mosaic-measure-settings-label input { width: 100% !important; min-width: 0; }
          .mosaic-measure-units { flex: 0 1 116px; min-width: 0; }
          .mosaic-measure-units select { width: 100% !important; min-width: 0; padding-right: 26px !important; font-size: 13px; }
          .mosaic-measure-actions { flex-shrink: 0; gap: 4px !important; }
          .mosaic-measure-actions .btn { width: 35px !important; height: 36px !important; }
          .mosaic-measure-separator { display: none !important; }
          .mosaic-measure-toolbar { border-bottom: 1px solid #394655; }
          .mosaic-measure-toolbar-title { display: none; }
          .mosaic-measure-toolbar-zoom { display: none; }
          .mosaic-measure-toolbar-reset { display: none !important; }
          .mosaic-measure-workspace { display: flex !important; flex-direction: column; flex: none !important; gap: 8px !important; }
          .mosaic-measure-viewport { flex: none !important; width: 100%; height: min(64vh, 650px); min-height: 260px !important; }
          .mosaic-measure-image { max-width: 100% !important; max-height: 62vh !important; }
          .mosaic-measure-legend { flex: none !important; width: 100%; max-width: none !important; min-width: 0 !important; margin-top: 0 !important; padding: 8px 10px !important; }
          .mosaic-measure-help-toggle { display: block; color: #fff; font-weight: 700; background: transparent; border: 0; padding: 4px 0; cursor: pointer; }
          .mosaic-measure-help-content { display: none; }
          .mosaic-measure-help-content.is-open { display: block; padding-top: 8px; }
          .mosaic-measure-footer { flex: none; margin-top: 8px !important; padding-bottom: 10px; }
        }
      `}</style>
      <div className="mosaic-measure-toolbar" style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0,
        padding: '4px 0 8px', borderBottom: '1px solid #394655', flexShrink: 0 }}>
        <div className="mosaic-measure-toolbar-scroll" style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1,
          overflowX: 'auto', whiteSpace: 'nowrap' }}>
        <strong className="mosaic-measure-toolbar-title" style={{ color: '#fff', whiteSpace: 'nowrap', fontSize: 16, marginRight: 6 }}>Measure Distances</strong>
        <span className="mosaic-measure-separator" aria-hidden="true" style={{ height: 28, borderLeft: '1px solid #516070', flexShrink: 0 }} />
        <div className="mosaic-measure-mode-group" role="group" aria-label="Measurement mode"
          style={{ display: 'flex', alignItems: 'stretch', gap: 0, borderRadius: 6,
            overflow: 'hidden', border: '1px solid #526273', flexShrink: 0 }}>
          <button type="button" className={`btn ${mode === 'reference' ? '' : 'btn-secondary'}`}
            aria-pressed={mode === 'reference'} onClick={() => setMode('reference')}
            style={{ margin: 0, fontWeight: 700, borderRadius: 0, whiteSpace: 'nowrap',
              border: 'none', borderRight: '1px solid #526273' }}>
            <i className="bx bx-target-lock" aria-hidden="true" /> Set Reference
          </button>
          <button type="button" className={`btn ${mode === 'measure' ? '' : 'btn-secondary'}`}
            disabled={!calibrated} aria-pressed={mode === 'measure'} onClick={() => setMode('measure')}
            style={{ margin: 0, fontWeight: 700, borderRadius: 0, whiteSpace: 'nowrap', border: 'none' }}>
            <i className="bx bx-ruler" aria-hidden="true" /> Measure Distance
          </button>
        </div>
        <div className="mosaic-measure-settings" style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <label className="mosaic-measure-settings-label" style={{ color: '#fff', margin: 0, display: 'flex', alignItems: 'center', gap: 5,
          whiteSpace: 'nowrap' }}>
          Reference
          <input type="number" min="0.000001" step="any" value={referenceValue}
            onChange={(event) => {
              const value = event.target.value;
              const numeric = Number(value);
              if (value.trim() !== '' && Number.isFinite(numeric) && numeric > 0) {
                lastValidCalibrationRef.current = { value: numeric, unit };
              }
              setReferenceValue(value);
            }}
            style={{ margin: 0, width: 86, height: 36 }} aria-label="Reference length" />
        </label>
        <label className="mosaic-measure-units" style={{ color: '#fff', margin: 0, display: 'flex', alignItems: 'center', gap: 5 }}>
          <span className="show-for-sr">Units</span>
          <select aria-label="Units" value={unit} onChange={(event) => {
            const next = event.target.value;
            const n = Number(referenceValue);
            if (referenceValue.trim() !== '' && Number.isFinite(n) && n > 0) {
              const converted = n * UNITS[unit] / UNITS[next];
              lastValidCalibrationRef.current = { value: converted, unit: next };
              setReferenceValue(String(converted));
            }
            setUnit(next);
          }} style={{ margin: 0, width: 104, height: 36, paddingRight: 26 }}>
            <option value="mm">mm</option><option value="cm">cm</option><option value="m">meters</option>
            <option value="in">inches</option><option value="ft">feet</option><option value="yd">yards</option>
          </select>
        </label>
        <div className="mosaic-measure-actions" role="group" aria-label="Measurement actions" style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <button type="button" className="btn btn-secondary radius"
            title="Clear distances (keep reference)" aria-label="Clear distances (keep reference)"
            style={{ margin: 0, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, verticalAlign: 'middle' }}
            disabled={!measurements.length} onClick={() => setMeasurements([])}>
            <i className="bx bx-eraser" aria-hidden="true" style={{ margin: 0, fontSize: 18, lineHeight: 1 }} />
          </button>
          <button type="button" className="btn btn-warning radius"
            title="Clear everything (reference and distances)" aria-label="Clear everything (reference and distances)"
            style={{ margin: 0, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, verticalAlign: 'middle' }}
            disabled={!reference && !measurements.length} onClick={resetReference}>
            <i className="bx bx-reset" aria-hidden="true" style={{ margin: 0, fontSize: 18, lineHeight: 1 }} />
          </button>
          <button type="button" className="btn btn-success radius"
            title="Download annotated image" aria-label="Download annotated image"
            style={{ margin: 0, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, verticalAlign: 'middle' }}
            disabled={!calibrated || exporting} onClick={downloadAnnotatedImage}>
            <i className={`bx ${exporting ? 'bx-loader-alt bx-spin' : 'bx-download'}`} aria-hidden="true" style={{ margin: 0, fontSize: 18, lineHeight: 1 }} />
          </button>
        </div>
        </div>
        </div>
        <span className="mosaic-measure-toolbar-zoom" style={{ color: '#d0d9e4', whiteSpace: 'nowrap', flexShrink: 0 }}>Zoom {Math.round(zoom * 100)}%</span>
        <button type="button" className="btn btn-secondary radius mosaic-measure-toolbar-reset" style={{ margin: 0, flexShrink: 0 }}
          onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}>Reset View</button>
        <button type="button" className="btn btn-danger radius mosaic-measure-close" onClick={closeAndSave}
          style={{ margin: 0, whiteSpace: 'nowrap', flexShrink: 0 }}>
          <i className="bx bx-x" aria-hidden="true" /> Close
        </button>
      </div>
      {(!Number.isFinite(referenceMeters) || referenceMeters <= 0) &&
        <div role="alert" style={{ color: '#ffb3b3' }}>Enter a reference length greater than zero.</div>}
      {saveStatus && <span role="status" style={{ color: saveStatus === 'Saved' ? '#9ddca8' : '#d0d9e4', fontSize: 12 }}>{saveStatus}</span>}
      {error && <div role="alert" style={{ color: '#ffb3b3' }}>{error}</div>}
      <div className="mosaic-measure-workspace" style={{ display: 'flex', flex: 1, minHeight: 0, minWidth: 0, gap: 12, flexWrap: 'wrap' }}>
      <div className="mosaic-measure-viewport" ref={viewportRef} onWheel={handleWheel} style={{ minHeight: 280, minWidth: 0, flex: '1 1 420px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        title="Scroll to zoom; Shift+drag or middle-mouse drag to pan">
        {dimensions && <div ref={imageRef} style={{ position: 'relative', width: 'fit-content', maxWidth: '100%', maxHeight: '100%',
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: 'center center' }}>
          <img className="mosaic-measure-image" src={imageUrl} alt="Stitched mosaic for measuring"
            draggable={false} style={{ display: 'block', maxWidth: '100%', maxHeight: '65vh', width: 'auto', height: 'auto' }} />
          <svg ref={svgRef} viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
            preserveAspectRatio="none" aria-label="Drag to draw a measurement line"
            onPointerDown={start} onPointerMove={move} onPointerUp={finish}
            onPointerCancel={(event) => { touchPointersRef.current.delete(event.pointerId); if (!touchPointersRef.current.size) touchGestureRef.current = null; drawingRef.current = null; panRef.current = null; setDraft(null); }}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', touchAction: 'none', cursor: 'crosshair' }}>
            {reference && drawLine(reference, '#f04444', 'reference')}
            {measurements.map((line, index) => drawLine(line, '#2688ee', index))}
            {draft && drawLine(draft, mode === 'reference' ? '#f04444' : '#2688ee', 'draft')}
          </svg>
          {reference && lineLabel(reference, '#f04444',
            `Reference: ${format(Number(referenceValue))} ${unit}`, 'reference-label')}
          {measurements.map((line, index) => lineLabel(line, '#2688ee',
            calibrated ? `#${index + 1}: ${format(distance(line))} ${unit}` : '', `label-${index}`))}
          {draft && mode === 'measure' && calibrated && lineLabel(draft, '#2688ee',
            `#${measurements.length + 1}: ${format(distance(draft))} ${unit}`, 'draft-label')}
        </div>}
      </div>
        <aside className="mosaic-measure-legend" aria-label="Measurement controls legend" style={{
          flex: '0 1 210px', minWidth: 170, maxWidth: 240,
          background: '#1d2733', border: '1px solid #394655', borderRadius: 8,
          padding: 12, color: '#d0d9e4', fontSize: 13, lineHeight: 1.55,
          alignSelf: 'flex-start', marginTop: 12
        }}>
          <button type="button" className="mosaic-measure-help-toggle" aria-expanded={showMobileHelp} onClick={() => setShowMobileHelp((value) => !value)}>Controls {showMobileHelp ? "▴" : "▾"}</button>
          <div className={`mosaic-measure-help-content ${showMobileHelp ? 'is-open' : ''}`}>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 15, marginBottom: 8 }} className="mosaic-measure-toolbar-title">Controls</div>
          <div style={{ marginBottom: 8 }}>
            <span style={{ color: '#ff8989', fontWeight: 700 }}>Red line</span>
            <div>Set Reference, then drag across a known length.</div>
          </div>
          <div style={{ marginBottom: 8 }}>
            <span style={{ color: '#85beff', fontWeight: 700 }}>Blue lines</span>
            <div>Measure Distances, then drag between points.</div>
          </div>
          <div style={{ borderTop: '1px solid #394655', paddingTop: 8 }}>
            <div><strong style={{ color: '#fff' }}>Two-finger pinch / drag</strong> — Zoom and pan on touch screens</div>
            <div><strong style={{ color: '#fff' }}>Mouse wheel</strong> — Zoom in/out</div>
            <div><strong style={{ color: '#fff' }}>Shift + drag</strong> — Pan image</div>
            <div><strong style={{ color: '#fff' }}>Middle drag</strong> — Pan image</div>
            <div><strong style={{ color: '#fff' }}>Reset View</strong> — Fit image</div>
            <div><strong style={{ color: '#fff' }}>Esc</strong> — Cancel or close</div>
          </div>
          <div style={{ borderTop: '1px solid #394655', paddingTop: 8, marginTop: 8 }}>
            <div><i className="bx bx-eraser" aria-hidden="true" /> Clear blue distances</div>
            <div><i className="bx bx-reset" aria-hidden="true" /> Clear all / recalibrate</div>
            <div><i className="bx bx-download" aria-hidden="true" /> Download annotated PNG</div>
          </div>
          </div>
        </aside>
      </div>
      <div className="mosaic-measure-footer" style={{ marginTop: 12, color: '#d0d9e4' }}>
        {measurements.length > 0 && calibrated &&
          <div>{measurements.map((line, index) => (
            <span key={index} style={{ display: 'inline-block', marginRight: 14 }}>
              #{index + 1}: {format(distance(line))} {unit}{' '}
              <button type="button" onClick={() => setMeasurements((current) => current.filter((_, i) => i !== index))}
                aria-label={`Remove measurement ${index + 1}`} style={{ background: 'transparent', color: '#fff', cursor: 'pointer' }}>×</button>
            </span>
          ))}</div>}
      </div>
    </div>
  );
};

export default MosaicDistanceViewer;
